/*

Modified from Fabric's GroupHeader.styles.ts

*/
import {
    IStyleFunction,
    getGlobalClassNames,
    IStyle,
    getFocusStyle,
    AnimationVariables,
    FontWeights,
    IconFontSizes,
    HighContrastSelector,
    getHighContrastNoAdjustStyle,
} from '@fluentui/react';
import {
    IGroupedComboBoxGroupHeaderStyleProps,
    IGroupedComboBoxGroupHeaderStyles,
} from './GroupedComboBoxGroupHeader.types';
import { assert } from 'textfield-with-action';

const GlobalClassNames = {
    root: 'ms-GroupHeader',
    compact: 'ms-GroupHeader--compact',
    check: 'ms-GroupHeader-check',
    icon: 'ms-GroupHeader-icon',
    expand: 'ms-GroupHeader-expand',
    isCollapsed: 'is-collapsed',
    title: 'ms-GroupHeader-title',
    isSelected: 'is-selected',
    iconTag: 'ms-Icon--Tag',
    group: 'ms-GroupedList-group',
    isDropping: 'is-dropping',
};

const beziers = {
    easeOutCirc: 'cubic-bezier(0.075, 0.820, 0.165, 1.000)',
    easeOutSine: 'cubic-bezier(0.390, 0.575, 0.565, 1.000)',
    easeInBack: 'cubic-bezier(0.600, -0.280, 0.735, 0.045)',
};

export const getStyles: IStyleFunction<
    IGroupedComboBoxGroupHeaderStyleProps,
    IGroupedComboBoxGroupHeaderStyles
> = (props) => {
    const { theme, className, isCollapsed } = props;
    const { cellLeftPadding } = { cellLeftPadding: 12 }; // padding from the source to align GroupHeader title with DetailsRow's first cell.
    const finalRowHeight = 32;

    const { palette, fonts } = theme;

    assert(theme);
    const classNames = getGlobalClassNames(GlobalClassNames, theme);

    const checkExpandResetStyles: IStyle = [
        getFocusStyle(theme, {
            highContrastStyle: {
                outline: '2px dashed ButtonText',
                ...getHighContrastNoAdjustStyle(),
            },
        }),
        {
            cursor: 'default',
            background: 'none',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0, // cancel default <button> padding
        },
    ];

    return {
        root: [
            classNames.root,
            theme.fonts.medium,
            {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: '1px',
                cursor: 'default',
                userSelect: 'none',
                selectors: {
                    [`:global(.${classNames.group}.${classNames.isDropping})`]: {
                        selectors: {
                            [`& > .${classNames.root}`]: {
                                transition:
                                    `transform ${AnimationVariables.durationValue4} ${beziers.easeOutCirc} ` +
                                    `opacity ${AnimationVariables.durationValue1} ${beziers.easeOutSine}`,
                                transitionDelay: AnimationVariables.durationValue3,
                                opacity: 1,
                                transform: 'rotate(0.2deg) scale(1);', // rotation prevents jittery motion in IE
                            },

                            [`.${classNames.check}`]: {
                                opacity: 0,
                            },
                        },
                    },
                    [':focus']: { outline: 'none' },
                },
            },
            className,
        ],
        groupHeaderContainer: [
            {
                display: 'flex',
                alignItems: 'center',
                height: finalRowHeight,
            },
        ],
        expand: [
            classNames.expand,
            checkExpandResetStyles,
            {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: fonts.small.fontSize,
                width: 32,
                height: finalRowHeight,
                color: palette.neutralSecondary,
                ':hover': {
                    backgroundColor: palette.neutralLight,
                },
                ':active': {
                    backgroundColor: palette.neutralQuaternaryAlt,
                },
                [HighContrastSelector]: {
                    ':hover': {
                        zIndex: 1,
                        backgroundColor: 'Highlight',
                        color: 'HighlightText',
                        outline: '2px dashed ButtonText',
                    },
                    ':active': {
                        zIndex: 1,
                        backgroundColor: 'Highlight',
                        color: 'HighlightText',
                        outline: '2px solid ButtonText',
                    },
                    ':focus': {
                        zIndex: 1,
                        backgroundColor: 'Highlight',
                        color: 'HighlightText',
                    },
                    ...getHighContrastNoAdjustStyle(),
                },
            },
        ],
        expandIsCollapsed: [
            {
                width: '12px',
                height: '12px',
            },
            isCollapsed
                ? [
                      classNames.isCollapsed,
                      {
                          transform: 'rotate(0deg)',
                          transformOrigin: '50% 50%',
                          transition: 'transform .1s linear',
                      },
                  ]
                : {
                      transform: 'rotate(180deg)',
                      transformOrigin: '50% 50%',
                      transition: 'transform .1s linear',
                  },
        ],
        title: [
            classNames.title,
            {
                paddingLeft: cellLeftPadding,
                textAlign: 'start',
                fontSize: fonts.medium.fontSize,
                fontWeight: FontWeights.semibold,
                lineHeight: '20px',
                outline: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                margin: 0,
                flexGrow: 1,
            },
        ],
        icon: [
            classNames.icon,
            {
                fontSize: IconFontSizes.large,
                color: palette.neutralSecondary,
                paddingLeft: cellLeftPadding,
            },
        ],
    };
};
